import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    Package, Plus, Edit3, Trash2, Eye, EyeOff, Star,
    Search, RefreshCw, Tag, Globe,
    Save, X, ExternalLink,
    CheckCircle2, Grid, List,
    FolderOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const ICONS = ['📦', '🏛️', '📑', '🗣️', '🗺️', '🏰', '🚨', '📊', '📱', '🤖', '🌐', '🔒', '🏢', '🎓', '💡', '🛡️', '📡', '⚙️', '🔑', '💼', '🌍', '📈', '🎯', '🔔', '📋'];

const TARGET_MARKETS = [
    { value: 'government', label: '🏢 სამთავრობო (Gov)', color: '#7c3aed' },
    { value: 'gov', label: '🏢 სამთავრობო (Gov)', color: '#7c3aed' },
    { value: 'education', label: '🎓 განათლება (Edu)', color: '#059669' },
    { value: 'edu', label: '🎓 განათლება (Edu)', color: '#059669' },
    { value: 'business', label: '💼 ბიზნეს (Business)', color: '#d97706' },
    { value: 'municipal', label: '🏛️ მუნიციპალური (Municipal)', color: '#2563eb' },
    { value: 'personal', label: '👤 პირადი (Personal)', color: '#0891b2' },
    { value: 'other', label: '🌍 სხვა (Other)', color: '#64748b' },
    { value: 'all', label: '🌍 ყველა სექტორი', color: '#64748b' },
];

const getSectorBadge = (market) => {
    switch (market) {
        case 'government':
        case 'gov':
            return { label: '🏢 Gov (სამთავრობო)', color: '#7c3aed', bg: '#f5f3ff' };
        case 'education':
        case 'edu':
            return { label: '🎓 Edu (განათლება)', color: '#059669', bg: '#ecfdf5' };
        case 'business':
            return { label: '💼 Business (ბიზნესი)', color: '#d97706', bg: '#fffbeb' };
        case 'municipal':
            return { label: '🏛️ Municipal (მუნიციპალური)', color: '#2563eb', bg: '#eff6ff' };
        case 'personal':
            return { label: '👤 Personal (პირადი)', color: '#0891b2', bg: '#ecfeff' };
        default:
            return { label: '🌍 Other (სხვა)', color: '#64748b', bg: '#f8fafc' };
    }
};

const PRICING_MODELS = [
    { value: 'one_time', label: 'ერთჯერადი ლიცენზია' },
    { value: 'subscription', label: 'თვიური/წლიური გამოწერა' },
    { value: 'one_time_plus_support', label: 'ლიცენზია + მხარდაჭერა (ARR)' },
    { value: 'quote', label: 'ფასი მოთხოვნით (Custom Quote)' },
];

const BADGE_COLORS = [
    '#2563eb', '#7c3aed', '#059669', '#dc2626', '#d97706',
    '#0891b2', '#9333ea', '#4f46e5', '#0d9488', '#be185d',
    '#1d4ed8', '#15803d', '#b91c1c', '#92400e', '#6d28d9',
];

const emptyProduct = {
    category_id: '',
    name_ka: '', name_en: '',
    tagline_ka: '', tagline_en: '',
    description_ka: '', description_en: '',
    icon: '📦', badge_color: '#6366f1',
    thumbnail: '',
    target_market: 'municipal',
    setup_price: '', monthly_price: '', annual_price: '',
    pricing_model: 'one_time_plus_support',
    features_ka: [], features_en: [],
    tech_stack: '',
    demo_url: '', docs_url: '',
    is_active: 1, is_featured: 0,
    sort_order: 0,
};

const emptyCategory = {
    name_ka: '', name_en: '', icon: '📁', color: '#6366f1',
    description_ka: '', description_en: '', sort_order: 0,
};

// ── INLINE FEATURE EDITOR ────────────────────────────────────────────────────
const FeatureEditor = ({ features, onChange, placeholder }) => {
    const [input, setInput] = useState('');
    const add = () => {
        if (input.trim()) {
            onChange([...features, input.trim()]);
            setInput('');
        }
    };
    return (
        <div>
            <div className="d-flex gap-2 mb-2">
                <input
                    type="text"
                    className="shadcn-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder={placeholder}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    style={{ fontSize: '13px' }}
                />
                <button type="button" className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={add}>
                    <Plus size={14} />
                </button>
            </div>
            <div className="d-flex flex-column gap-1">
                {features.map((f, i) => (
                    <div key={i} className="d-flex align-items-center justify-content-between p-2 bg-light rounded border small">
                        <span>✓ {f}</span>
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 ms-2"
                            onClick={() => onChange(features.filter((_, idx) => idx !== i))}
                        >
                            <X size={12} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── PRODUCT CARD (Grid View) ─────────────────────────────────────────────────
const ProductCard = ({ product, categories, onEdit, onDelete, onToggleActive, onToggleFeatured }) => {
    const cat = categories.find(c => c.id === product.category_id);
    return (
        <div
            className="shadcn-card h-100 position-relative overflow-hidden"
            style={{
                borderLeft: `5px solid ${product.badge_color || '#6366f1'}`,
                opacity: product.is_active ? 1 : 0.65,
            }}
        >
            {/* Featured ribbon */}
            {product.is_featured ? (
                <div style={{
                    position: 'absolute', top: 0, right: 0,
                    background: '#f59e0b', color: '#fff',
                    fontSize: '10px', fontWeight: 700,
                    padding: '2px 10px', borderBottomLeftRadius: '8px'
                }}>
                    ⭐ FEATURED
                </div>
            ) : null}

            <div className="p-3 d-flex flex-column h-100">
                {/* Header */}
                <div className="d-flex align-items-start gap-3 mb-3">
                    <div
                        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                        style={{ width: 52, height: 52, background: `${product.badge_color}20`, fontSize: 28 }}
                    >
                        {product.icon || '📦'}
                    </div>
                    <div className="overflow-hidden">
                        <h6 className="fw-bold text-dark mb-0 small text-truncate">{product.name_ka}</h6>
                        <div className="text-muted smallest text-truncate">{product.name_en}</div>
                        {cat && (
                            <span
                                className="badge rounded-pill smallest mt-1"
                                style={{ background: `${cat.color}20`, color: cat.color, border: `1px solid ${cat.color}40` }}
                            >
                                {cat.icon} {cat.name_ka}
                            </span>
                        )}
                    </div>
                </div>

                {/* Tagline */}
                {product.tagline_ka && (
                    <p className="smallest text-muted mb-2 text-truncate">{product.tagline_ka}</p>
                )}

                {/* Pricing */}
                <div className="p-2 rounded bg-light border mb-3">
                    <div className="row g-0 text-center">
                        {product.setup_price > 0 && (
                            <div className="col border-end">
                                <div className="fw-bold text-primary small">₾{parseFloat(product.setup_price).toLocaleString()}</div>
                                <div className="smallest text-muted">Setup</div>
                            </div>
                        )}
                        {product.annual_price > 0 && (
                            <div className="col">
                                <div className="fw-bold text-success small">₾{parseFloat(product.annual_price).toLocaleString()}</div>
                                <div className="smallest text-muted">ARR/წელი</div>
                            </div>
                        )}
                        {product.monthly_price > 0 && (
                            <div className={`col ${product.setup_price > 0 || product.annual_price > 0 ? 'border-start' : ''}`}>
                                <div className="fw-bold text-indigo small" style={{ color: '#6366f1' }}>₾{parseFloat(product.monthly_price).toLocaleString()}</div>
                                <div className="smallest text-muted">თვე</div>
                            </div>
                        )}
                        {product.pricing_model === 'quote' && (
                            <div className="col">
                                <div className="fw-bold text-dark small">Custom</div>
                                <div className="smallest text-muted">მოთხოვნით</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Badges row */}
                <div className="d-flex flex-wrap gap-1 mb-3">
                    {product.target_market && (() => {
                        const s = getSectorBadge(product.target_market);
                        return (
                            <span className="badge rounded-pill smallest" style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}35` }}>
                                {s.label}
                            </span>
                        );
                    })()}
                    {product.tech_stack && (
                        <span className="badge bg-light text-muted border smallest text-truncate" style={{ maxWidth: '150px' }}>
                            ⚙️ {product.tech_stack}
                        </span>
                    )}
                </div>

                {/* Features preview */}
                {product.features_ka?.length > 0 && (
                    <div className="mb-3 flex-grow-1">
                        <div className="smallest text-muted fw-bold mb-1">მახასიათებლები:</div>
                        {product.features_ka.slice(0, 3).map((f, i) => (
                            <div key={i} className="d-flex align-items-center gap-1 smallest text-dark mb-1">
                                <CheckCircle2 size={11} className="text-success flex-shrink-0" />
                                <span>{f}</span>
                            </div>
                        ))}
                        {product.features_ka.length > 3 && (
                            <div className="smallest text-muted">+{product.features_ka.length - 3} სხვა...</div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex gap-1 flex-wrap mt-auto pt-2 border-top">
                    <button
                        className="shadcn-btn shadcn-btn-primary shadcn-btn-sm flex-grow-1"
                        onClick={() => onEdit(product)}
                        style={{ fontSize: '11px' }}
                    >
                        <Edit3 size={12} /> რედაქტირება
                    </button>
                    <button
                        className={`shadcn-btn shadcn-btn-sm ${product.is_active ? 'shadcn-btn-secondary' : 'shadcn-btn-secondary'}`}
                        onClick={() => onToggleActive(product)}
                        title={product.is_active ? 'გამორთვა' : 'ჩართვა'}
                        style={{ fontSize: '11px', color: product.is_active ? '#16a34a' : '#dc2626' }}
                    >
                        {product.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                    </button>
                    <button
                        className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary"
                        onClick={() => onToggleFeatured(product)}
                        title={product.is_featured ? 'Featured-ის გაუქმება' : 'Featured-ად მარკირება'}
                        style={{ fontSize: '11px', color: product.is_featured ? '#f59e0b' : '#94a3b8' }}
                    >
                        <Star size={12} />
                    </button>
                    <button
                        className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary"
                        onClick={() => onDelete(product)}
                        title="წაშლა"
                        style={{ fontSize: '11px', color: '#dc2626' }}
                    >
                        <Trash2 size={12} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const AdminProducts = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterMarket, setFilterMarket] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'active' | 'inactive' | 'featured'

    // Modals
    const [showProductModal, setShowProductModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingProduct] = useState(null); // setter unused; product always null (editing navigates to /admin/products/:id)
    const [editingCategory, setEditingCategory] = useState(null);
    const [deletingProduct, setDeletingProduct] = useState(null);
    const [saving, setSaving] = useState(false);

    // Product form state
    const [form, setForm] = useState({ ...emptyProduct });
    const [catForm, setCatForm] = useState({ ...emptyCategory });
    const [activeFormTab, setActiveFormTab] = useState('basic'); // 'basic' | 'pricing' | 'features' | 'seo'

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [pRes, cRes] = await Promise.all([
                fetchWithSecurity(`${API_URL}products.php`),
                fetchWithSecurity(`${API_URL}products.php?type=categories`)
            ]);
            const [pData, cData] = await Promise.all([pRes.json(), cRes.json()]);
            if (Array.isArray(pData)) setProducts(pData);
            if (Array.isArray(cData)) setCategories(cData);
        } catch (err) {
            toast.error('პროდუქტების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, []);

    useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

    // ── FILTER ────────────────────────────────────────────────────────────────
    const filtered = products.filter(p => {
        const matchSearch = !searchTerm.trim() ||
            p.name_ka?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.tagline_ka?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchCat = filterCategory === 'all' || String(p.category_id) === String(filterCategory);
        const matchMarket = filterMarket === 'all' || p.target_market === filterMarket;
        let matchStatus = true;
        if (filterStatus === 'active') matchStatus = p.is_active === 1;
        else if (filterStatus === 'inactive') matchStatus = p.is_active !== 1;
        else if (filterStatus === 'featured') matchStatus = p.is_featured === 1;
        return matchSearch && matchCat && matchMarket && matchStatus;
    });

    // ── OPEN PRODUCT FORM ─────────────────────────────────────────────────────
    const openAddProduct = () => {
        navigate('/admin/products/add');
    };

    const openEditProduct = (product) => {
        navigate(`/admin/products/edit/${product.id}`);
    };

    // ── SAVE PRODUCT ──────────────────────────────────────────────────────────
    const handleSaveProduct = async () => {
        if (!form.name_ka.trim() || !form.name_en.trim()) {
            toast.error('სახელი ქართულად და ინგლისურად სავალდებულოა!');
            return;
        }
        setSaving(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}products.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: editingProduct ? 'update' : 'create',
                    id: editingProduct?.id,
                    ...form,
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(editingProduct ? 'პროდუქტი განახლდა!' : 'პროდუქტი დაემატა!');
                setShowProductModal(false);
                fetchAll();
            } else {
                toast.error(data.error || 'შენახვა ვერ მოხერხდა');
            }
        } catch {
            toast.error('შენახვა ვერ მოხერხდა');
        }
        setSaving(false);
    };

    // ── TOGGLE ACTIVE / FEATURED ──────────────────────────────────────────────
    const handleToggleActive = async (product) => {
        const newVal = product.is_active === 1 ? 0 : 1;
        try {
            await fetchWithSecurity(`${API_URL}products.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'toggle_active', id: product.id, is_active: newVal })
            });
            setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_active: newVal } : p));
        } catch { toast.error('განახლება ვერ მოხერხდა'); }
    };

    const handleToggleFeatured = async (product) => {
        const newVal = product.is_featured === 1 ? 0 : 1;
        try {
            await fetchWithSecurity(`${API_URL}products.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'toggle_featured', id: product.id, is_featured: newVal })
            });
            setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_featured: newVal } : p));
            toast.success(newVal ? '⭐ Featured-ად მოინიშნა!' : 'Featured სტატუსი გაუქმდა');
        } catch { toast.error('განახლება ვერ მოხერხდა'); }
    };

    // ── DELETE ────────────────────────────────────────────────────────────────
    const handleDeleteProduct = async () => {
        if (!deletingProduct) return;
        try {
            await fetchWithSecurity(`${API_URL}products.php`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: deletingProduct.id })
            });
            toast.success('პროდუქტი წაიშალა');
            setShowDeleteModal(false);
            setDeletingProduct(null);
            fetchAll();
        } catch { toast.error('წაშლა ვერ მოხერხდა'); }
    };

    // ── CATEGORY SAVE ─────────────────────────────────────────────────────────
    const handleSaveCategory = async () => {
        if (!catForm.name_ka.trim() || !catForm.name_en.trim()) {
            toast.error('კატეგორიის სახელი სავალდებულოა!');
            return;
        }
        setSaving(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}products.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: editingCategory ? 'update_category' : 'create_category',
                    id: editingCategory?.id,
                    ...catForm
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(editingCategory ? 'კატეგორია განახლდა!' : 'კატეგორია დაემატა!');
                setShowCategoryModal(false);
                fetchAll();
            } else toast.error(data.error || 'შენახვა ვერ მოხერხდა');
        } catch { toast.error('შენახვა ვერ მოხერხდა'); }
        setSaving(false);
    };

    const handleDeleteCategory = async (cat) => {
        if (!window.confirm(`წაშლა: "${cat.name_ka}"? პროდუქტები კატეგორიის გარეშე დარჩება.`)) return;
        try {
            await fetchWithSecurity(`${API_URL}products.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete_category', id: cat.id })
            });
            toast.success('კატეგორია წაიშალა');
            fetchAll();
        } catch { toast.error('წაშლა ვერ მოხერხდა'); }
    };

    // ── STATS ──────────────────────────────────────────────────────────────────
    const stats = {
        total: products.length,
        active: products.filter(p => p.is_active === 1).length,
        featured: products.filter(p => p.is_featured === 1).length,
        totalSetupValue: products.reduce((acc, p) => acc + parseFloat(p.setup_price || 0), 0),
        totalARR: products.reduce((acc, p) => acc + parseFloat(p.annual_price || 0), 0),
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title="Orbitronix პროდუქტების კატალოგი">
            {/* ── HEADER ── */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <h4 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                        <Package size={24} className="text-primary" />
                        Orbitronix ციფრული პროდუქტების კატალოგი
                    </h4>
                    <span className="text-muted small">
                        მართეთ ყველა ციფრული პროდუქტი, ფასი, კატეგორია და მახასიათებლები
                    </span>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchAll}>
                        <RefreshCw size={14} /> განახლება
                    </button>
                    <button
                        className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                        onClick={() => { setEditingCategory(null); setCatForm({ ...emptyCategory }); setShowCategoryModal(true); }}
                    >
                        <Tag size={14} /> კატეგორიის დამატება
                    </button>
                    <button className="shadcn-btn shadcn-btn-primary" onClick={openAddProduct}>
                        <Plus size={16} /> პროდუქტის დამატება
                    </button>
                </div>
            </div>

            {/* ── STATS STRIP ── */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-2">
                    <div className="shadcn-card p-3 text-center">
                        <div className="fw-bold fs-4 text-dark">{stats.total}</div>
                        <div className="smallest text-muted">სულ პროდუქტი</div>
                    </div>
                </div>
                <div className="col-6 col-md-2">
                    <div className="shadcn-card p-3 text-center" style={{ borderLeft: '4px solid #16a34a' }}>
                        <div className="fw-bold fs-4 text-success">{stats.active}</div>
                        <div className="smallest text-muted">აქტიური</div>
                    </div>
                </div>
                <div className="col-6 col-md-2">
                    <div className="shadcn-card p-3 text-center" style={{ borderLeft: '4px solid #f59e0b' }}>
                        <div className="fw-bold fs-4" style={{ color: '#d97706' }}>{stats.featured}</div>
                        <div className="smallest text-muted">Featured</div>
                    </div>
                </div>
                <div className="col-6 col-md-2">
                    <div className="shadcn-card p-3 text-center" style={{ borderLeft: '4px solid #6366f1' }}>
                        <div className="fw-bold fs-4" style={{ color: '#4f46e5' }}>{categories.length}</div>
                        <div className="smallest text-muted">კატეგორია</div>
                    </div>
                </div>
                <div className="col-6 col-md-2">
                    <div className="shadcn-card p-3 text-center" style={{ borderLeft: '4px solid #2563eb' }}>
                        <div className="fw-bold small text-primary">₾{stats.totalSetupValue.toLocaleString()}</div>
                        <div className="smallest text-muted">სულ Setup Value</div>
                    </div>
                </div>
                <div className="col-6 col-md-2">
                    <div className="shadcn-card p-3 text-center" style={{ borderLeft: '4px solid #059669' }}>
                        <div className="fw-bold small text-success">₾{stats.totalARR.toLocaleString()}</div>
                        <div className="smallest text-muted">სულ ARR პოტენციალი</div>
                    </div>
                </div>
            </div>

            {/* ── CATEGORIES CHIPS BAR ── */}
            {categories.length > 0 && (
                <div className="shadcn-card p-3 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <FolderOpen size={16} className="text-muted" />
                        <span className="small fw-bold text-dark">კატეგორიები:</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <button
                            className={`btn btn-sm ${filterCategory === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '12px' }}
                            onClick={() => setFilterCategory('all')}
                        >
                            🌐 ყველა ({products.length})
                        </button>
                        {categories.map(cat => {
                            const count = products.filter(p => String(p.category_id) === String(cat.id)).length;
                            return (
                                <div key={cat.id} className="d-flex align-items-center gap-0">
                                    <button
                                        className="btn btn-sm"
                                        style={{
                                            fontSize: '12px',
                                            background: filterCategory === String(cat.id) ? cat.color : `${cat.color}18`,
                                            color: filterCategory === String(cat.id) ? '#fff' : cat.color,
                                            border: `1px solid ${cat.color}50`,
                                            borderRight: 0,
                                            borderRadius: '6px 0 0 6px'
                                        }}
                                        onClick={() => setFilterCategory(filterCategory === String(cat.id) ? 'all' : String(cat.id))}
                                    >
                                        {cat.icon} {cat.name_ka} ({count})
                                    </button>
                                    <button
                                        className="btn btn-sm"
                                        style={{
                                            fontSize: '11px',
                                            background: `${cat.color}10`,
                                            color: cat.color,
                                            border: `1px solid ${cat.color}50`,
                                            borderRadius: '0 6px 6px 0',
                                            padding: '3px 6px'
                                        }}
                                        onClick={() => { setEditingCategory(cat); setCatForm({ ...cat }); setShowCategoryModal(true); }}
                                        title="კატეგორიის რედაქტირება"
                                    >
                                        <Edit3 size={11} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── FILTER & SEARCH BAR ── */}
            <div className="shadcn-card p-3 mb-4">
                <div className="row g-2 align-items-center">
                    <div className="col-12 col-md-4">
                        <div className="position-relative">
                            <Search size={14} className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                            <input
                                type="text"
                                className="shadcn-input ps-4"
                                placeholder="პროდუქტის ძებნა..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-6 col-md-2">
                        <select className="shadcn-input" value={filterMarket} onChange={e => setFilterMarket(e.target.value)}>
                            <option value="all">ყველა სეგმენტი</option>
                            {TARGET_MARKETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div className="col-6 col-md-2">
                        <select className="shadcn-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                            <option value="all">ყველა სტატუსი</option>
                            <option value="active">✅ მხოლოდ აქტიური</option>
                            <option value="inactive">❌ მხოლოდ გამორთული</option>
                            <option value="featured">⭐ მხოლოდ Featured</option>
                        </select>
                    </div>
                    <div className="col-auto ms-auto d-flex gap-1">
                        <button
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'grid' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('grid')}
                            title="ბადის ხედი"
                        ><Grid size={14} /></button>
                        <button
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'list' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('list')}
                            title="სიის ხედი"
                        ><List size={14} /></button>
                    </div>
                    <div className="col-auto">
                        <span className="text-muted smallest">
                            {filtered.length} / {products.length} პროდუქტი
                        </span>
                    </div>
                </div>
            </div>

            {/* ── PRODUCTS GRID ── */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                    <div className="text-muted mt-2">პროდუქტები იტვირთება...</div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <Package size={48} className="mb-3 opacity-25" />
                    <div className="fw-bold">პროდუქტები ვერ მოიძებნა</div>
                    <div className="small mt-1">სხვა საძიებო კრიტერიუმი სცადეთ ან დაამატეთ ახალი პროდუქტი</div>
                    <button className="shadcn-btn shadcn-btn-primary mt-3" onClick={openAddProduct}>
                        <Plus size={16} /> პირველი პროდუქტის დამატება
                    </button>
                </div>
            ) : viewMode === 'grid' ? (
                <div className="row g-3">
                    {filtered.map(product => (
                        <div key={product.id} className="col-12 col-md-6 col-xl-4">
                            <ProductCard
                                product={product}
                                categories={categories}
                                onEdit={openEditProduct}
                                onDelete={p => { setDeletingProduct(p); setShowDeleteModal(true); }}
                                onToggleActive={handleToggleActive}
                                onToggleFeatured={handleToggleFeatured}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                /* ── LIST VIEW ── */
                <div className="shadcn-card">
                    <div className="table-responsive">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>პროდუქტი</th>
                                    <th>კატეგორია</th>
                                    <th>სექტორი (Sector)</th>
                                    <th className="text-end">Setup (₾)</th>
                                    <th className="text-end">ARR (₾)</th>
                                    <th className="text-center">სტატუსი</th>
                                    <th className="text-center">Featured</th>
                                    <th className="text-center">მოქმედება</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(product => {
                                    const cat = categories.find(c => c.id === product.category_id);
                                    const s = getSectorBadge(product.target_market);
                                    return (
                                        <tr key={product.id} style={{ opacity: product.is_active ? 1 : 0.6 }}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: '20px' }}>{product.icon}</span>
                                                    <div>
                                                        <div className="fw-bold text-dark small">{product.name_ka}</div>
                                                        <div className="text-muted smallest">{product.name_en}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {cat ? (
                                                    <span className="badge rounded-pill smallest" style={{ background: `${cat.color}20`, color: cat.color, border: `1px solid ${cat.color}40` }}>
                                                        {cat.icon} {cat.name_ka}
                                                    </span>
                                                ) : <span className="text-muted smallest">—</span>}
                                            </td>
                                            <td>
                                                <span className="badge rounded-pill smallest" style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}35` }}>
                                                    {s.label}
                                                </span>
                                            </td>
                                            <td className="text-end fw-semibold text-primary small">
                                                {product.setup_price > 0 ? `₾${parseFloat(product.setup_price).toLocaleString()}` : '—'}
                                            </td>
                                            <td className="text-end fw-semibold text-success small">
                                                {product.annual_price > 0 ? `₾${parseFloat(product.annual_price).toLocaleString()}` : '—'}
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={() => handleToggleActive(product)}
                                                    title={product.is_active ? 'აქტიური - დასაუქმებლად დააჭირეთ' : 'გამორთული - გასააქტიურებლად დააჭირეთ'}
                                                >
                                                    {product.is_active
                                                        ? <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25">✅ აქტიური</span>
                                                        : <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25">❌ გამორთული</span>}
                                                </button>
                                            </td>
                                            <td className="text-center">
                                                <button className="btn btn-sm btn-link p-0" onClick={() => handleToggleFeatured(product)}>
                                                    <Star size={16} style={{ color: product.is_featured ? '#f59e0b' : '#cbd5e1', fill: product.is_featured ? '#f59e0b' : 'none' }} />
                                                </button>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-1">
                                                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => openEditProduct(product)} style={{ fontSize: '11px', padding: '2px 8px' }}>
                                                        <Edit3 size={12} /> რედ.
                                                    </button>
                                                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={() => { setDeletingProduct(product); setShowDeleteModal(true); }} style={{ fontSize: '11px', padding: '2px 6px', color: '#dc2626' }}>
                                                        <Trash2 size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
                PRODUCT ADD/EDIT MODAL
            ═══════════════════════════════════════════════════════════ */}
            {showProductModal && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.55)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow-xl" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            {/* Modal Header */}
                            <div className="modal-header border-bottom py-3 px-4" style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)' }}>
                                <div>
                                    <h5 className="modal-title fw-bold text-white mb-0">
                                        {editingProduct ? `✏️ ${editingProduct.name_ka}` : '➕ ახალი პროდუქტის დამატება'}
                                    </h5>
                                    <span className="text-white text-opacity-70 smallest">
                                        Orbitronix ციფრული პროდუქტების კატალოგი
                                    </span>
                                </div>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowProductModal(false)} />
                            </div>

                            {/* Tab Nav */}
                            <div className="bg-light border-bottom px-4 pt-3">
                                <div className="d-flex gap-1">
                                    {[
                                        { key: 'basic', label: '📝 ძირითადი', icon: null },
                                        { key: 'pricing', label: '💰 ფასები', icon: null },
                                        { key: 'features', label: '✓ მახასიათებლები', icon: null },
                                        { key: 'links', label: '🔗 ბმულები & Meta', icon: null },
                                    ].map(tab => (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            className={`btn btn-sm rounded-top-3 rounded-bottom-0 px-3 py-2 ${activeFormTab === tab.key ? 'btn-primary text-white' : 'btn-outline-secondary bg-white border-bottom-0'}`}
                                            style={{ fontSize: '12px', marginBottom: '-1px', border: activeFormTab === tab.key ? undefined : '1px solid #dee2e6' }}
                                            onClick={() => setActiveFormTab(tab.key)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body p-4">
                                {/* TAB: BASIC */}
                                {activeFormTab === 'basic' && (
                                    <div className="row g-3">
                                        {/* Icon & Badge Color row */}
                                        <div className="col-12">
                                            <div className="p-3 rounded-3 bg-light border">
                                                <div className="row g-3 align-items-center">
                                                    <div className="col-auto">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 64, height: 64, background: `${form.badge_color}20`, fontSize: 36 }}>
                                                                {form.icon}
                                                            </div>
                                                            <div>
                                                                <div className="smallest text-muted fw-bold mb-1">ემოჯი-ხატულა:</div>
                                                                <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '280px' }}>
                                                                    {ICONS.map(ico => (
                                                                        <button
                                                                            key={ico}
                                                                            type="button"
                                                                            className="btn p-1"
                                                                            style={{
                                                                                fontSize: '18px', lineHeight: 1,
                                                                                background: form.icon === ico ? `${form.badge_color}30` : 'transparent',
                                                                                border: form.icon === ico ? `2px solid ${form.badge_color}` : '2px solid transparent',
                                                                                borderRadius: '6px'
                                                                            }}
                                                                            onClick={() => setForm({ ...form, icon: ico })}
                                                                        >{ico}</button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col">
                                                        <div className="smallest text-muted fw-bold mb-1">ბეჯის ფერი:</div>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {BADGE_COLORS.map(color => (
                                                                <button
                                                                    key={color}
                                                                    type="button"
                                                                    onClick={() => setForm({ ...form, badge_color: color })}
                                                                    style={{
                                                                        width: 28, height: 28, borderRadius: '50%',
                                                                        background: color, border: form.badge_color === color ? '3px solid #000' : '2px solid #fff',
                                                                        boxShadow: form.badge_color === color ? '0 0 0 2px ' + color : '0 1px 3px rgba(0,0,0,0.2)',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                />
                                                            ))}
                                                            <input
                                                                type="color"
                                                                value={form.badge_color}
                                                                onChange={e => setForm({ ...form, badge_color: e.target.value })}
                                                                style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid #e2e8f0', cursor: 'pointer', padding: 0 }}
                                                                title="Custom ფერი"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Names */}
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">სახელი ქართულად 🇬🇪 <span className="text-danger">*</span></label>
                                            <input type="text" className="shadcn-input" value={form.name_ka} onChange={e => setForm({ ...form, name_ka: e.target.value })} placeholder="მაგ: მუნიციპალური პორტალი" />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">Name in English 🇬🇧 <span className="text-danger">*</span></label>
                                            <input type="text" className="shadcn-input" value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} placeholder="e.g. Municipal Portal" />
                                        </div>

                                        {/* Taglines */}
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">ტეგლაინი ქართულად 🇬🇪</label>
                                            <input type="text" className="shadcn-input" value={form.tagline_ka} onChange={e => setForm({ ...form, tagline_ka: e.target.value })} placeholder="მოკლე სარეკლამო ფრაზა..." />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">Tagline in English 🇬🇧</label>
                                            <input type="text" className="shadcn-input" value={form.tagline_en} onChange={e => setForm({ ...form, tagline_en: e.target.value })} placeholder="Short marketing phrase..." />
                                        </div>

                                        {/* Category + Target Market */}
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">კატეგორია</label>
                                            <select className="shadcn-input" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                                                <option value="">-- კატეგორია არ არის --</option>
                                                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name_ka}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">სამიზნე ბაზარი</label>
                                            <select className="shadcn-input" value={form.target_market} onChange={e => setForm({ ...form, target_market: e.target.value })}>
                                                {TARGET_MARKETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                        </div>

                                        {/* Descriptions */}
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">აღწერა ქართულად 🇬🇪</label>
                                            <textarea className="shadcn-input" rows={4} value={form.description_ka} onChange={e => setForm({ ...form, description_ka: e.target.value })} placeholder="სრული აღწერა ქართულად..." />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">Description in English 🇬🇧</label>
                                            <textarea className="shadcn-input" rows={4} value={form.description_en} onChange={e => setForm({ ...form, description_en: e.target.value })} placeholder="Full description in English..." />
                                        </div>

                                        {/* Tech Stack + Thumbnail */}
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">ტექნოლოგიური სტეკი (Tech Stack)</label>
                                            <input type="text" className="shadcn-input" value={form.tech_stack} onChange={e => setForm({ ...form, tech_stack: e.target.value })} placeholder="მაგ: React, PHP, MySQL, Node.js" />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">Thumbnail URL (სურათის ბმული)</label>
                                            <input type="text" className="shadcn-input" value={form.thumbnail} onChange={e => setForm({ ...form, thumbnail: e.target.value })} placeholder="https://..." />
                                        </div>

                                        {/* Flags */}
                                        <div className="col-12">
                                            <div className="d-flex gap-3">
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" id="is_active" checked={form.is_active === 1} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
                                                    <label className="form-check-label small fw-semibold" htmlFor="is_active">✅ აქტიური (გამოჩნდება საიტზე)</label>
                                                </div>
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" id="is_featured" checked={form.is_featured === 1} onChange={e => setForm({ ...form, is_featured: e.target.checked ? 1 : 0 })} />
                                                    <label className="form-check-label small fw-semibold" htmlFor="is_featured">⭐ Featured (ხაზგასმული)</label>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <label className="form-label fw-semibold small mb-0">რიგი:</label>
                                                    <input type="number" className="shadcn-input" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} style={{ width: '70px' }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* TAB: PRICING */}
                                {activeFormTab === 'pricing' && (
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="p-3 rounded-3 bg-light border mb-2">
                                                <label className="form-label fw-bold small">ფასობრივი მოდელი</label>
                                                <div className="row g-2">
                                                    {PRICING_MODELS.map(pm => (
                                                        <div key={pm.value} className="col-12 col-md-6">
                                                            <div
                                                                className={`p-3 rounded-3 border cursor-pointer ${form.pricing_model === pm.value ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}
                                                                onClick={() => setForm({ ...form, pricing_model: pm.value })}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <input type="radio" readOnly checked={form.pricing_model === pm.value} className="form-check-input mt-0" />
                                                                    <span className="fw-semibold small">{pm.label}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold small">საწყისი ლიცენზია / Setup (₾ GEL)</label>
                                            <div className="position-relative">
                                                <span className="position-absolute top-50 translate-middle-y ms-2 fw-bold text-muted">₾</span>
                                                <input type="number" className="shadcn-input ps-4" value={form.setup_price} onChange={e => setForm({ ...form, setup_price: e.target.value })} placeholder="0" min="0" step="500" />
                                            </div>
                                            <div className="smallest text-muted mt-1">ერთჯერადი ინტეგრაციის + ლიცენზიის ღირებულება</div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold small">წლიური მხარდაჭერა (ARR) (₾ GEL)</label>
                                            <div className="position-relative">
                                                <span className="position-absolute top-50 translate-middle-y ms-2 fw-bold text-muted">₾</span>
                                                <input type="number" className="shadcn-input ps-4" value={form.annual_price} onChange={e => setForm({ ...form, annual_price: e.target.value })} placeholder="0" min="0" step="500" />
                                            </div>
                                            <div className="smallest text-muted mt-1">ყოველწლიური განახლება, ჰოსტინგი, სატექნიკო მხარდაჭერა</div>
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold small">თვიური გამოწერა (MRR) (₾ GEL)</label>
                                            <div className="position-relative">
                                                <span className="position-absolute top-50 translate-middle-y ms-2 fw-bold text-muted">₾</span>
                                                <input type="number" className="shadcn-input ps-4" value={form.monthly_price} onChange={e => setForm({ ...form, monthly_price: e.target.value })} placeholder="0" min="0" step="100" />
                                            </div>
                                            <div className="smallest text-muted mt-1">თვიური SaaS გამოწერა (სურვილისამებრ)</div>
                                        </div>

                                        {/* Pricing Preview */}
                                        {(parseFloat(form.setup_price) > 0 || parseFloat(form.annual_price) > 0) && (
                                            <div className="col-12">
                                                <div className="p-3 rounded-3" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(16,185,129,0.06))', border: '1px solid rgba(99,102,241,0.2)' }}>
                                                    <div className="fw-bold small text-dark mb-2">💡 3-წლიანი კონტრაქტის ჯამური ღირებულება:</div>
                                                    <div className="row g-2">
                                                        <div className="col-auto">
                                                            <div className="p-2 bg-white rounded border text-center">
                                                                <div className="fw-bold text-primary small">₾{parseFloat(form.setup_price || 0).toLocaleString()}</div>
                                                                <div className="smallest text-muted">Setup</div>
                                                            </div>
                                                        </div>
                                                        <div className="col-auto">
                                                            <div className="p-2 bg-white rounded border text-center">
                                                                <div className="fw-bold text-success small">₾{(parseFloat(form.annual_price || 0) * 3).toLocaleString()}</div>
                                                                <div className="smallest text-muted">3 წლის ARR</div>
                                                            </div>
                                                        </div>
                                                        <div className="col-auto">
                                                            <div className="p-2 rounded border text-center" style={{ background: '#f5f3ff' }}>
                                                                <div className="fw-bold small" style={{ color: '#7c3aed' }}>
                                                                    ₾{(parseFloat(form.setup_price || 0) + parseFloat(form.annual_price || 0) * 3).toLocaleString()}
                                                                </div>
                                                                <div className="smallest text-muted">ჯამი 3 წელი</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* TAB: FEATURES */}
                                {activeFormTab === 'features' && (
                                    <div className="row g-4">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">მახასიათებლები ქართულად 🇬🇪</label>
                                            <FeatureEditor
                                                features={form.features_ka}
                                                onChange={features => setForm({ ...form, features_ka: features })}
                                                placeholder="დაამატეთ მახასიათებელი ქართულად..."
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">Features in English 🇬🇧</label>
                                            <FeatureEditor
                                                features={form.features_en}
                                                onChange={features => setForm({ ...form, features_en: features })}
                                                placeholder="Add feature in English..."
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* TAB: LINKS */}
                                {activeFormTab === 'links' && (
                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">
                                                <ExternalLink size={14} className="me-1" />
                                                Demo URL (ცოცხალი დემო)
                                            </label>
                                            <input type="url" className="shadcn-input" value={form.demo_url} onChange={e => setForm({ ...form, demo_url: e.target.value })} placeholder="https://demo.example.com" />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold small">
                                                <Globe size={14} className="me-1" />
                                                Docs URL (დოკუმენტაცია)
                                            </label>
                                            <input type="url" className="shadcn-input" value={form.docs_url} onChange={e => setForm({ ...form, docs_url: e.target.value })} placeholder="https://docs.example.com" />
                                        </div>
                                        <div className="col-12">
                                            <div className="p-3 bg-light rounded-3 border">
                                                <div className="small fw-bold text-dark mb-2">📊 ID & სხვა ინფო:</div>
                                                <div className="d-flex flex-wrap gap-3 smallest text-muted">
                                                    {editingProduct && <span>ID: <strong>{editingProduct.id}</strong></span>}
                                                    {editingProduct && <span>Slug: <code>{editingProduct.slug}</code></span>}
                                                    {editingProduct?.created_at && <span>შეიქმნა: <strong>{new Date(editingProduct.created_at).toLocaleDateString('ka-GE')}</strong></span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer bg-light border-top d-flex justify-content-between">
                                <span className="text-muted smallest">
                                    Orbitronix Product Catalogue — {editingProduct ? 'რედაქტირება' : 'ახალი პროდუქტი'}
                                </span>
                                <div className="d-flex gap-2">
                                    <button className="shadcn-btn shadcn-btn-secondary" onClick={() => setShowProductModal(false)}>
                                        <X size={14} /> გაუქმება
                                    </button>
                                    <button className="shadcn-btn shadcn-btn-primary" onClick={handleSaveProduct} disabled={saving}>
                                        {saving ? <><div className="spinner-border spinner-border-sm me-2" />ინახება...</> : <><Save size={14} /> შენახვა</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ CATEGORY MODAL ═══ */}
            {showCategoryModal && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
                    <div className="modal-dialog">
                        <div className="modal-content border-0 shadow-xl" style={{ borderRadius: '16px' }}>
                            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)' }}>
                                <h5 className="modal-title fw-bold text-white">
                                    {editingCategory ? `✏️ ${editingCategory.name_ka}` : '➕ ახალი კატეგორია'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCategoryModal(false)} />
                            </div>
                            <div className="modal-body p-4">
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label fw-bold small">სახელი ქართულად 🇬🇪 <span className="text-danger">*</span></label>
                                        <input type="text" className="shadcn-input" value={catForm.name_ka} onChange={e => setCatForm({ ...catForm, name_ka: e.target.value })} placeholder="მაგ: ციფრული მმართველობა" />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label fw-bold small">Name in English 🇬🇧 <span className="text-danger">*</span></label>
                                        <input type="text" className="shadcn-input" value={catForm.name_en} onChange={e => setCatForm({ ...catForm, name_en: e.target.value })} placeholder="e.g. Digital Governance" />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label fw-bold small">ხატულა (Icon)</label>
                                        <div className="d-flex flex-wrap gap-1">
                                            {ICONS.slice(0, 12).map(ico => (
                                                <button key={ico} type="button" className="btn p-1" style={{ fontSize: '20px', background: catForm.icon === ico ? '#f0f0f0' : 'transparent', border: catForm.icon === ico ? '2px solid #6366f1' : '2px solid transparent', borderRadius: '6px' }} onClick={() => setCatForm({ ...catForm, icon: ico })}>{ico}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label fw-bold small">ფერი (Color)</label>
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            {BADGE_COLORS.slice(0, 8).map(color => (
                                                <button key={color} type="button" onClick={() => setCatForm({ ...catForm, color })} style={{ width: 26, height: 26, borderRadius: '50%', background: color, border: catForm.color === color ? '3px solid #000' : '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', cursor: 'pointer' }} />
                                            ))}
                                            <input type="color" value={catForm.color} onChange={e => setCatForm({ ...catForm, color: e.target.value })} style={{ width: 26, height: 26, borderRadius: '50%', border: '2px solid #e2e8f0', cursor: 'pointer', padding: 0 }} />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-bold small">აღწერა (სურვილისამებრ)</label>
                                        <input type="text" className="shadcn-input" value={catForm.description_ka} onChange={e => setCatForm({ ...catForm, description_ka: e.target.value })} placeholder="კატეგორიის მოკლე აღწერა..." />
                                    </div>
                                    {editingCategory && (
                                        <div className="col-12">
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteCategory(editingCategory)}>
                                                <Trash2 size={13} /> კატეგორიის წაშლა
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-top">
                                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => setShowCategoryModal(false)}><X size={14} /> გაუქმება</button>
                                <button className="shadcn-btn shadcn-btn-primary" onClick={handleSaveCategory} disabled={saving}>
                                    {saving ? <><div className="spinner-border spinner-border-sm me-2" />ინახება...</> : <><Save size={14} /> შენახვა</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ DELETE CONFIRM MODAL ═══ */}
            {showDeleteModal && deletingProduct && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1080 }}>
                    <div className="modal-dialog modal-sm">
                        <div className="modal-content border-0 shadow" style={{ borderRadius: '16px' }}>
                            <div className="modal-body p-4 text-center">
                                <div style={{ fontSize: '48px' }} className="mb-3">🗑️</div>
                                <h5 className="fw-bold text-dark">პროდუქტის წაშლა?</h5>
                                <p className="text-muted small">
                                    <strong>{deletingProduct.name_ka}</strong> — ეს ქმედება შეუქცევადია.
                                </p>
                                <div className="d-flex justify-content-center gap-2 mt-3">
                                    <button className="shadcn-btn shadcn-btn-secondary" onClick={() => { setShowDeleteModal(false); setDeletingProduct(null); }}>
                                        გაუქმება
                                    </button>
                                    <button className="btn btn-danger" onClick={handleDeleteProduct}>
                                        <Trash2 size={14} /> წაშლა
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminProducts;
